import { Component, Inject, Injector, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { RegistrationRequest } from "@dto/registrationRequest";
import { Address } from "@models/address";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { User } from "@models/identity/user";
import { AuthenticationService } from "@services/authentication/authentication.service";
import { PasswordConfirmationValidatorService } from "@services/custom-validators/password-confirm-validator.service";
import { UsersService } from "@services/entity/users.service";
import { FormService } from "@services/form/form.service";
import { NgxSpinnerService } from "ngx-spinner";
import { AGBComponent } from "../agb/agb.component";
import { DatasecurityComponent } from "../datasecurity/datasecurity.component";
import { MessageService } from "@services/message/message.service";
import { ToastService } from "@services/toast/toast.service";
import { Observable } from "rxjs";
import { UpdateAcceptAdRequestDto } from "../../../dto/updateAcceptAdRequestDto";
import { UserDto } from "@dto/userDto";

@Component({
  selector: "app-register-user",
  templateUrl: "./register-user.component.html",
  styleUrls: ["./register-user.component.scss"],
})
export class RegisterUserComponent implements OnInit {
  private token: string;
  public user: UserDto;
  public isOperatorView: boolean = false;
  public navigationUrl: string;
  public formCreated: boolean;

  public passwordHasUpperCase: boolean = false;
  public passwordHasLowerCase: boolean = false;
  public passwordHasDigit: boolean = false;
  public passwordHasLength: boolean = false;
  public passwordHasSpecialCharacter: boolean = false;
  public passwordQuality: number = 0;
  public passwordIsMatching: boolean = false;
  public passwordRating: string = "";
  public passwordRatings: Array<string> = [
    "",
    "Sehr Schwach",
    "Schwach",
    "Ausreichend",
    "Gut",
    "Stark",
    "Sehr Stark",
    "Super Stark",
    "Stark wie Krupp Stahl",
  ];

  public whitelabel: string;
  public whitelabelURL: string =
    "https://cubosgeneralstorage.blob.core.windows.net/whitelabel-storage/";
  public emailExists: boolean;

  currentYearLong(): number {
    return new Date().getFullYear();
  }

  constructor(
    @Inject("BASE_URL") private baseUrl: string,
    private authenticationService: AuthenticationService,
    public formService: FormService,
    public messageService: MessageService,
    private passwordConfirmValidator: PasswordConfirmationValidatorService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private activatedRoute: ActivatedRoute,
    private usersService: UsersService,
    private toastService: ToastService,
    private dialog: NgbModal,
    injector: Injector
  ) {}

  ngOnInit(): void {
    this.token = this.activatedRoute.snapshot.queryParams["token"];

    if (!this.isOperatorView) {
      if (this.token) {
        var email = this.authenticationService.getTokenNameClaim(this.token);

        this.authenticationService
          .getUserByEmailForRegistration(email, this.token)
          .subscribe(
            (result) => {
              this.user = result;
              this.createForm();
            },
            (error) => {
              this.router.navigate(["/auth/used-token-feedback"]);
            }
          );
      } else {
        this.createForm();
      }
    } else {
      this.CreateOperatorForm();
    }
  }

  ngAfterViewInit() {
    this.whitelabel = localStorage.getItem("whitelabel");
  }

  private CreateOperatorForm() {
    this.formService.validators = {
      postalCode: [
        Validators.required,
        Validators.pattern("^[0-9]*$"),
        Validators.maxLength(6),
        Validators.minLength(4),
      ],
      city: [Validators.required],
      street: [Validators.required],
      houseNumber: [Validators.required],
      country: [],
      email: [Validators.required, Validators.email],
      firstName: [Validators.required, Validators.maxLength(50)],
      lastName: [Validators.required, Validators.maxLength(50)],
      gender: [Validators.required],
      type: [Validators.required],
      company: [],
      phone: [Validators.required],
      acceptTos: [Validators.requiredTrue],
      acceptPrivacy: [Validators.requiredTrue],
      acceptAd: [],
    };

    var addressGroup = new UntypedFormGroup({
      postalCode: new UntypedFormControl(
        "",
        this.formService.validators["postalCode"]
      ),
      city: new UntypedFormControl("", this.formService.validators["city"]),
      street: new UntypedFormControl("", this.formService.validators["street"]),
      houseNumber: new UntypedFormControl(
        "",
        this.formService.validators["houseNumber"]
      ),
      country: new UntypedFormControl(
        "DE",
        this.formService.validators["country"]
      ),
    });

    this.formService.form = new UntypedFormGroup({
      email: new UntypedFormControl("", this.formService.validators["email"]),
      firstName: new UntypedFormControl(
        "",
        this.formService.validators["firstName"]
      ),
      lastName: new UntypedFormControl(
        "",
        this.formService.validators["lastName"]
      ),
      gender: new UntypedFormControl("", this.formService.validators["gender"]),
      type: new UntypedFormControl(
        "company",
        this.formService.validators["type"]
      ),
      company: new UntypedFormControl(
        "",
        this.formService.validators["company"]
      ),
      phone: new UntypedFormControl("", this.formService.validators["phone"]),
      address: addressGroup,

      password: new UntypedFormControl("newCustomer2022"),
      confirm: new UntypedFormControl("newCustomer2022"),
      acceptTos: new UntypedFormControl(false),
      acceptPrivacy: new UntypedFormControl(false),
      acceptAd: new UntypedFormControl(false),
    });

    this.formService.form
      .get("confirm")
      .setValidators([
        Validators.required,
        this.passwordConfirmValidator.validateConfirmPassword(
          this.formService.form.get("password")
        ),
      ]);

    this.formService.formMap = new Map<string, UntypedFormGroup>();
    this.formService.formMap.set("address", addressGroup);

    this.formCreated = true;
  }

  private createForm() {
    this.formService.validators = {
      agbConfirm: [Validators.requiredTrue],
      wrbConfirm: [Validators.requiredTrue],
      dsbConfirm: [Validators.requiredTrue],

      postalCode: [
        Validators.required,
        Validators.pattern("^[0-9]*$"),
        Validators.maxLength(6),
        Validators.minLength(4),
      ],
      // city: [Validators.required, Validators.pattern('^[\u00C0-\u017Fa-zA-Z]+(?:[\\s-][\u00C0-\u017Fa-zA-Z]+)*$')],
      city: [Validators.required],
      //  street: [Validators.required, Validators.pattern('^[\u00C0-\u017Fa-zA-Z.]+(?:[\\s-][\u00C0-\u017Fa-zA-Z.]+)*$')],
      street: [Validators.required],
      houseNumber: [Validators.required],
      country: [],
      email: [Validators.required, Validators.email],
      //  firstName: [Validators.required, Validators.pattern('^[\u00C0-\u017Fa-zA-Z]+(?:[\\s-][\u00C0-\u017Fa-zA-Z]+)*$'), Validators.maxLength(50)],
      firstName: [Validators.required, Validators.maxLength(50)],
      //  lastName: [Validators.required, Validators.pattern('^[\u00C0-\u017Fa-zA-Z]+(?:[\\s-][\u00C0-\u017Fa-zA-Z]+)*$'), Validators.maxLength(50)],
      lastName: [Validators.required, Validators.maxLength(50)],
      gender: [Validators.required],
      type: [Validators.required],
      company: [],
      // phone: [Validators.required, Validators.pattern('^([+](\\d{1,3})\\s?)?((\\(\\d{3,5}\\)|\\d{3,5})(\\s)?)\\d{3,8}$'), Validators.minLength(6)],
      phone: [
        Validators.required,
        Validators.pattern("[- +()0-9]+"),
        Validators.minLength(6),
      ],

      password: [
        Validators.required,
        Validators.pattern(/(?=.*[A-Z])(?=.*[a-z])(?=.*\d)/g),
        Validators.minLength(10),
        this.passwordConfirmValidator.validateMatch("confirm", true),
      ],
      confirm: [
        Validators.required,
        Validators.pattern(/(?=.*[A-Z])(?=.*[a-z])(?=.*\d)/g),
        Validators.minLength(10),
        this.passwordConfirmValidator.validateMatch("password"),
      ],
    };

    var addressGroup = new UntypedFormGroup({
      postalCode: new UntypedFormControl(
        "",
        this.formService.validators["postalCode"]
      ),
      city: new UntypedFormControl("", this.formService.validators["city"]),
      street: new UntypedFormControl("", this.formService.validators["street"]),
      houseNumber: new UntypedFormControl(
        "",
        this.formService.validators["houseNumber"]
      ),
      country: new UntypedFormControl(
        "DE",
        this.formService.validators["country"]
      ),
    });

    this.formService.form = new UntypedFormGroup({
      email: new UntypedFormControl(
        this.user?.email,
        this.formService.validators["email"]
      ),
      firstName: new UntypedFormControl(
        this.user?.firstName,
        this.formService.validators["firstName"]
      ),
      lastName: new UntypedFormControl(
        this.user?.lastName,
        this.formService.validators["lastName"]
      ),
      gender: new UntypedFormControl("", this.formService.validators["gender"]),
      type: new UntypedFormControl(
        "private",
        this.formService.validators["type"]
      ),
      company: new UntypedFormControl(
        "",
        this.formService.validators["company"]
      ),
      phone: new UntypedFormControl("", this.formService.validators["phone"]),
      address: addressGroup,

      password: new UntypedFormControl(
        "",
        this.formService.validators["password"]
      ),
      confirm: new UntypedFormControl(
        "",
        this.formService.validators["confirm"]
      ),

      agbConfirm: new UntypedFormControl(
        "",
        this.formService.validators["agbConfirm"]
      ),
      wrbConfirm: new UntypedFormControl(
        "",
        this.formService.validators["wrbConfirm"]
      ),
      dsbConfirm: new UntypedFormControl(
        "",
        this.formService.validators["dsbConfirm"]
      ),
      infoConfirm: new UntypedFormControl(""),
    });

    // this.formService.form.get('confirm').setValidators([
    //   Validators.required,
    //   this.passwordConfirmValidator.validateConfirmPassword(this.formService.form.get('password'))
    // ]);

    this.formService.formMap = new Map<string, UntypedFormGroup>();
    this.formService.formMap.set("address", addressGroup);

    this.formCreated = true;
  }

  public isTypeCompany() {
    if (this.formService.form.controls["type"].value == "company") {
      this.formService.form.get("company").setValidators([Validators.required]);
      this.formService.form.get("company").updateValueAndValidity();
      return true;
    }
    this.formService.form.get("company").clearValidators();
    this.formService.form.get("company").updateValueAndValidity();
    return false;
  }

  public onCancelClick(): void {
    this.dialog.dismissAll();
  }

  validateAllFormFields(formGroup: UntypedFormGroup) {
    //{1}
    Object.keys(formGroup.controls).forEach((field) => {
      //{2}
      const control = formGroup.get(field); //{3}
      if (control instanceof UntypedFormControl) {
        //{4}
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        //{5}
        this.validateAllFormFields(control); //{6}
      }
    });
  }

  public registerUser(registerFormValue) {
    this.spinner.show();

    this.messageService.clearErrors();
    const formValues = { ...registerFormValue };

    if (this.isOperatorView) {
      this.token = null;
    }

    const registrationRequest: RegistrationRequest = {
      email: formValues.email,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      gender: formValues.gender,
      type: formValues.type,
      company: formValues.company,
      phoneNumber: formValues.phone,
      address: new Address(formValues.address),
      acceptAd: formValues.acceptAd ? formValues.acceptAd : false,
      password: formValues.password,
      confirmPassword: formValues.confirm,
      clientURI: this.baseUrl + "auth/emailconfirmation",

      token: this.token,
    };

    if (this.token) {
      this.authenticationService
        .registerUserWithInvite(registrationRequest)
        .subscribe(
          (result) => {
            this.router.navigate(["/auth/register-success-with-invite"]);
            this.usersService
              .updateAcceptAd({
                UserId: this.user.id,
                AcceptAd: this.formService.form.get("infoConfirm")?.value,
              } as UpdateAcceptAdRequestDto)
              .subscribe(
                (result) => {
                  this.spinner.hide();
                },
                (error) => {
                  this.spinner.hide();
                  console.error(error);
                }
              );
          },
          (error) => {
            if (error.isRedirectError) {
              this.router.navigate(["/auth/register-success-with-invite"]);
            }
            this.messageService.setError(error);
            window.scroll(0, 0);
            this.spinner.hide();
          }
        );
    } else {
      if (this.isOperatorView) {
        this.navigationUrl = "/customer";
      } else {
        this.navigationUrl = "/auth/register-success";
      }
      this.authenticationService.registerUser(registrationRequest).subscribe(
        (result) => {
          this.router.navigate([this.navigationUrl]);
          this.usersService
            .updateAcceptAd({
              UserId: this.user.id,
              AcceptAd: this.formService.form.get("infoConfirm")?.value,
            } as UpdateAcceptAdRequestDto)
            .subscribe(
              (result) => {
                this.spinner.hide();
              },
              (error) => {
                this.spinner.hide();
                console.error(error);
              }
            );
        },
        (error) => {
          if (error.isRedirectError) {
            this.router.navigate(["/"]);
          }
          this.messageService.setError(error);
          window.scroll(0, 0);
          this.spinner.hide();
        }
      );
      if (this.isOperatorView) {
        this.navigationUrl = "/customer";
        this.router.navigate(["/customer"]);
        this.dialog.dismissAll();
        this.spinner.hide();
      }
    }
  }

  public registerUserFromOperator(registerFormValue) {
    this.validateAllFormFields(this.formService.form);

    this.spinner.show();

    this.messageService.clearErrors();
    const formValues = { ...registerFormValue };

    if (this.isOperatorView) {
      this.token = null;
    }

    const registrationRequest: RegistrationRequest = {
      email: formValues.email,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      gender: formValues.gender,
      type: formValues.type,
      company: formValues.company,
      phoneNumber: formValues.phone,
      address: new Address(formValues.address),
      acceptAd: formValues.acceptAd ? formValues.acceptAd : false,
      password: formValues.password,
      confirmPassword: formValues.confirm,
      clientURI: this.baseUrl + "auth/emailconfirmation",

      token: this.token,
    };

    if (this.token) {
      this.authenticationService
        .registerUserWithInvite(registrationRequest)
        .subscribe(
          (result) => {
            this.router.navigate(["/auth/register-success-with-invite"]);
            this.spinner.hide();
          },
          (error) => {
            if (error.isRedirectError) {
              this.router.navigate(["/auth/register-success-with-invite"]);
            }
            this.messageService.setError(error);
            window.scroll(0, 0);
            this.spinner.hide();
          }
        );
    } else {
      if (this.isOperatorView) {
        this.navigationUrl = "/customer";
      } else {
        this.navigationUrl = "/auth/register-success";
      }
      this.authenticationService
        .registerUserFromOperator(registrationRequest)
        .subscribe(
          (result) => {
            this.router.navigate([this.navigationUrl]);
            this.spinner.hide();
            this.toastService.success(
              $localize`:@@CustomerCreatedSuccessfulMessage:Der neue Kunde wurde erfolgreich angelegt!`
            );
            this.dialog.dismissAll();
          },
          (error) => {
            if (error.isRedirectError) {
              this.router.navigate(["/"]);
            }
            this.messageService.setError(error);
            window.scroll(0, 0);
            this.spinner.hide();
          }
        );
    }
  }

  navigateToLogin() {
    this.router.navigate(["/auth/login"]);
  }

  openAGBDialog() {
    const dialogRef = this.dialog.open(AGBComponent, {
      centered: true,
      backdrop: true,
    });

    dialogRef.result.then(
      (result) => {
        //this.ngOnInit();
      },
      (backdrop) => {}
    );
  }

  openDSDialog() {
    const dialogRef = this.dialog.open(DatasecurityComponent, {
      centered: true,
      backdrop: true,
    });

    dialogRef.result.then(
      (result) => {
        //this.ngOnInit();
      },
      (backdrop) => {}
    );
  }

  checkEmail() {
    var email = this.formService.form.get("email").value;
    if (email != "") {
      this.usersService.checkIfEmailExists(email).subscribe(
        (result) => {
          this.emailExists = result;
          if (this.emailExists) {
            this.formService.form.setErrors({
              emailExists: "E-Mail existiert bereits",
            });
            return false;
          }
        },
        (error) => {
          console.error(error);
        }
      );
    } else {
      this.emailExists = false;
      return true;
    }
  }

  checkPassword(p) {
    // This regex will enforce these rules:
    // At least one upper case English letter,  (?=.*?[A-Z])
    // At least one lower case English letter,  (?=.*?[a-z])
    // At least one digit,                      (?=.*?[0-9])
    // At least one special character,          (?=.*?[#?!@$%^&*-])
    // Minimum ten in length.                   {10,}

    // let requirements = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d){10,}$/

    this.passwordQuality = 0;

    let upperCase = /^(?=.*[A-Z])/g;
    this.passwordHasUpperCase = upperCase.test(p);
    if (
      upperCase.test(p) &&
      p.match(upperCase) &&
      p.length > this.passwordQuality * 2
    )
      this.passwordQuality += 1;

    let lowerCase = /^(?=.*[a-z])/g;
    this.passwordHasLowerCase = lowerCase.test(p);
    if (
      lowerCase.test(p) &&
      p.match(lowerCase) &&
      p.length > this.passwordQuality * 2
    )
      this.passwordQuality += 1;

    let digit = /^(?=.*\d)/g;
    this.passwordHasDigit = digit.test(p);
    if (digit.test(p) && p.match(digit) && p.length > this.passwordQuality * 2)
      this.passwordQuality += 1;

    this.passwordHasLength = p.length >= 10;
    this.passwordQuality += parseInt(Math.abs(p.length / 10).toFixed(0));

    let specialCharacter = /^(?=.*?[#?!@$%^&*-])/;
    this.passwordHasSpecialCharacter = specialCharacter.test(p);
    if (specialCharacter.test(p)) this.passwordQuality += 1;

    if (this.passwordQuality > this.passwordRatings.length - 1) {
      this.passwordRating = this.passwordRatings[this.passwordRatings.length];
    } else {
      this.passwordRating = this.passwordRatings[this.passwordQuality];
    }

    this.passwordIsMatching =
      this.formService.form.get("password").value ==
      this.formService.form.get("confirm").value;
  }

  trimValue(event) {
    const value = event.target.value.trim();
    event.target.value = value;
    const formControlName = event.target.getAttribute("formControlName");
    let formControl = this.formService.form.get(formControlName);
    if (!formControl) {
      formControl = this.formService.form.get(`address.${formControlName}`);
    }
    formControl.setValue(value);
    formControl.updateValueAndValidity({ onlySelf: true });
  }
}
